import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MapPinIcon,
  PlusIcon,
  ListBulletIcon,
  CheckCircleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';

// --- Utility Functions to replace moment.js ---

// Formats a Date object or string into 'MMM D, YYYY'
const formatDate = (dateString) => {
  const options = { year: 'numeric', month: 'short', day: 'numeric' };
  return new Date(dateString).toLocaleDateString(undefined, options);
};

// Formats a Date object or string into 'hh:mm A'
const formatTime = (date) => {
  return new Date(date).toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

// Gets today's date in 'YYYY-MM-DD' format for input default
const getTodayDateInput = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// --- Main Component ---

const FieldWorkReport = () => {
  const [report, setReport] = useState({
    date: getTodayDateInput(),
    clientName: '',
    activities: '',
    remarks: '',
    location: null,
  });

  // Updated mock data to use standard date strings compatible with new formatting
  const [submittedReports, setSubmittedReports] = useState([
    {
      id: 1,
      date: '2025-09-22',
      clientName: 'ABC Corp',
      activities: 'Initial client consultation and product demo.',
      location: '123 Business Lane, Delhi',
      submittedAt: formatTime(new Date()), // Use current time mock
    },
    {
      id: 2,
      date: '2025-09-21',
      clientName: 'XYZ Ltd',
      activities: 'Follow-up meeting and contract negotiation.',
      location: '456 Commercial Street, Mumbai',
      submittedAt: formatTime(new Date()), // Use current time mock
    },
  ]);

  const [submissionMessage, setSubmissionMessage] = useState('');

  // Handle input changes for the form fields
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setReport({ ...report, [name]: value });
  };

  // Handles simulating location capture
  const handleLocationCapture = () => {
    // In a real app, this would use the browser's Geolocation API.
    setReport({ ...report, location: 'Current Location Captured!' });
    setSubmissionMessage('Location captured!');
    setTimeout(() => setSubmissionMessage(''), 3000);
  };

  // Handles the final report submission
  const handleSubmit = (e) => {
    e.preventDefault();
    if (report.clientName && report.activities) {
      const newReport = {
        id: submittedReports.length + 1,
        ...report,
        location: report.location || 'Location not specified',
        submittedAt: formatTime(new Date()), // Capture time of submission
      };
      
      // Add new report to the top of the list
      setSubmittedReports([newReport, ...submittedReports]); 
      
      // Reset the form state
      setReport({
        date: getTodayDateInput(), // Reset date to today
        clientName: '',
        activities: '',
        remarks: '',
        location: null,
      });
      
      setSubmissionMessage('Report submitted successfully!');
      setTimeout(() => setSubmissionMessage(''), 3000);
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };
  
  // Custom font style applied to the whole app for required fonts
  const appStyle = {
    fontFamily: 'Roboto, Poppins, sans-serif',
  };

  return (
    // Background is plain white and font style is applied
    <div className="min-h-screen bg-white p-4 sm:p-8 text-gray-800" style={appStyle}>
      
      {/* Header Section */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-7xl mx-auto mb-8"
      >
        {/* Poppins font style for emphasis on headers */}
        <h1 className="text-3xl sm:text-4xl font-extrabold text-cyan-700" style={{ fontFamily: 'Poppins, sans-serif' }}>
          Field Work Report
        </h1>
        <p className="text-md sm:text-xl font-light text-cyan-600 mt-2">
          Submit your daily activities and log your location.
        </p>
      </motion.div>

      {/* Main Content Grid: Responsive Layout (1 column on mobile, 3 on large screens) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
        
        {/* Report Submission Form (LHS) */}
        <motion.div
          variants={cardVariants}
          initial="hidden"
          animate="visible"
          className="lg:col-span-1 bg-white p-6 rounded-2xl border border-gray-100 shadow-xl h-full flex flex-col"
        >
          <h3 className="text-2xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>Submit New Report</h3>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="date" className="block text-sm font-medium text-gray-700">Date</label>
              <input
                type="date"
                name="date"
                value={report.date}
                onChange={handleInputChange}
                className="mt-1 block w-full px-3 py-2 rounded-lg border border-gray-300 shadow-sm focus:border-cyan-500 focus:ring-cyan-500"
                required
              />
            </div>
            <div>
              <label htmlFor="clientName" className="block text-sm font-medium text-gray-700">Client/Project Name</label>
              <input
                type="text"
                name="clientName"
                value={report.clientName}
                onChange={handleInputChange}
                className="mt-1 block w-full px-3 py-2 rounded-lg border border-gray-300 shadow-sm focus:border-cyan-500 focus:ring-cyan-500"
                placeholder="e.g., Star Enterprises"
                required
              />
            </div>
            <div>
              <label htmlFor="activities" className="block text-sm font-medium text-gray-700">Activities Performed</label>
              <textarea
                name="activities"
                value={report.activities}
                onChange={handleInputChange}
                rows="4"
                className="mt-1 block w-full px-3 py-2 rounded-lg border border-gray-300 shadow-sm focus:border-cyan-500 focus:ring-cyan-500"
                placeholder="Describe your activities in detail."
                required
              ></textarea>
            </div>
            <div>
              <label htmlFor="remarks" className="block text-sm font-medium text-gray-700">Remarks (Optional)</label>
              <textarea
                name="remarks"
                value={report.remarks}
                onChange={handleInputChange}
                rows="2"
                className="mt-1 block w-full px-3 py-2 rounded-lg border border-gray-300 shadow-sm focus:border-cyan-500 focus:ring-cyan-500"
                placeholder="Any additional notes."
              ></textarea>
            </div>
            
            {/* Location Capture Button and Status */}
            <div className="flex items-center space-x-3">
              <button
                type="button"
                onClick={handleLocationCapture}
                className="w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-xl shadow-md text-sm font-medium text-white bg-indigo-500 hover:bg-indigo-600 transition duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <MapPinIcon className="h-5 w-5 mr-2" />
                Capture Location
              </button>
              {report.location && <CheckCircleIcon className="h-6 w-6 text-green-500 flex-shrink-0" />}
            </div>
            
            {/* Submit Button */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-xl shadow-lg text-white bg-cyan-600 hover:bg-cyan-700 transition duration-200 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 font-bold"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Submit Report
            </motion.button>
            
            {/* Submission Message */}
            <AnimatePresence>
              {submissionMessage && (
                <motion.p
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="mt-2 text-center text-sm font-semibold text-cyan-700 bg-cyan-100 rounded-full py-2"
                >
                  {submissionMessage}
                </motion.p>
              )}
            </AnimatePresence>
          </form>
        </motion.div>

        {/* Submission History Section (RHS) */}
        <motion.div
          variants={cardVariants}
          initial="hidden"
          animate="visible"
          transition={{ delay: 0.2 }}
          className="lg:col-span-2 bg-white p-6 rounded-2xl border border-gray-100 shadow-xl h-full flex flex-col"
        >
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Poppins, sans-serif' }}>My Reports History</h3>
            <ListBulletIcon className="h-7 w-7 text-cyan-600" />
          </div>
          
          {/* Responsive Table Container: forces horizontal scroll on small devices */}
          <div className="overflow-x-auto flex-1">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client/Project</th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Activities</th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Submitted At</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <AnimatePresence initial={false}>
                  {submittedReports.map((report) => (
                    <motion.tr
                      key={report.id}
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                      className="hover:bg-cyan-50/50"
                    >
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{formatDate(report.date)}</td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-600">{report.clientName}</td>
                      <td className="px-3 sm:px-6 py-4 text-sm text-gray-600 max-w-xs truncate" title={report.activities}>{report.activities}</td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-600">{report.location}</td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        <div className="flex items-center">
                          <ClockIcon className="h-4 w-4 mr-1 text-gray-400" />
                          {report.submittedAt}
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
          {submittedReports.length === 0 && (
              <p className="text-center text-gray-500 py-12">No field reports submitted yet.</p>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default FieldWorkReport;